import { lazy, Suspense, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import CanonicalUrl from "@/components/CanonicalUrl";
import LandingSchema from "@/components/LandingSchema";
import SeoHead from "@/components/SeoHead";
import { useLazySection } from "@/hooks/useLazySection";
import MobileStickyCTA from "@/components/MobileStickyCTA";

// Lazy load below-the-fold components
const Problem = lazy(() => import("@/components/Problem"));
const Solution = lazy(() => import("@/components/Solution"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Features = lazy(() => import("@/components/Features"));
const Comparison = lazy(() => import("@/components/Comparison"));
const SmartLinksSection = lazy(() => import("@/components/SmartLinksSection"));
const SmartBioSection = lazy(() => import("@/components/SmartBioSection"));
const CreatorCarousel = lazy(() => import("@/components/CreatorCarousel"));
const SocialLogos = lazy(() => import("@/components/SocialLogos"));
const Stats = lazy(() => import("@/components/Stats"));
const Testimonial = lazy(() => import("@/components/Testimonial"));
const ReferralBanner = lazy(() => import("@/components/ReferralBanner"));
const FreeVsProComparison = lazy(() => import("@/components/FreeVsProComparison"));
const Pricing = lazy(() => import("@/components/Pricing"));
const FAQ = lazy(() => import("@/components/FAQ"));
const CTAFooter = lazy(() => import("@/components/CTAFooter"));
const BlogPreviewSection = lazy(() => import("@/components/BlogPreviewSection"));
const SeoInternalLinks = lazy(() => import("@/components/SeoInternalLinks"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));
const LandingSocialProof = lazy(() => import("@/components/LandingSocialProof"));

const BASE = "https://tuttimieilink.com";

/** Placeholder that reserves vertical space for content-visibility: auto */
const SectionPlaceholder = () => (
  <div className="min-h-[200px]" />
);

const Index = () => {
  // Defer bottom-half sections until user scrolls near them
  const [bottomRef, bottomVisible] = useLazySection("400px");

  // Fire Google Ads `purchase` conversion when returning from Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") !== "true") return;
    const key = "__ads_purchase_fired__";
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    import("@/lib/ads-conversions").then((m) => {
      m.trackPurchase({ value: 9, currency: "EUR", label: "subscription" });
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SeoHead title="Link in Bio con Prenotazioni e Deeplink™ | TuttiIMieiLink" description="Il link in bio che fa vendere: prodotti, prenotazioni online e analytics in una pagina. Il Deeplink™ apre i link nel browser reale bypassando quello in-app: +40% conversioni. Gratis." path="/" />
      <CanonicalUrl path="/" />
      <BreadcrumbSchema items={[{ name: "Home", url: BASE }]} />
      <LandingSchema />
      <Navbar />
      <main>
        <Hero />
        {/* First fold — loads immediately after Hero */}
        <Suspense fallback={null}>
          <Problem />
          <Solution />
          <HowItWorks />
          <Features />
          <Comparison />
        </Suspense>

        {/* Second fold — deferred until user scrolls near */}
        <div ref={bottomRef} className="cv-auto-section">
          {bottomVisible ? (
            <Suspense fallback={<SectionPlaceholder />}>
              <SmartLinksSection />
              <SmartBioSection />
              <SocialLogos />
              <CreatorCarousel />
              <Stats />
              <Testimonial />
              <ReferralBanner />
              <FreeVsProComparison />
              <Pricing />
              <FAQ />
              <BlogPreviewSection />
              <SeoInternalLinks />
              <CTAFooter />
            </Suspense>
          ) : (
            <SectionPlaceholder />
          )}
        </div>
      </main>
      <MobileStickyCTA />
      <Suspense fallback={null}>
        <ExitIntentPopup />
        <LandingSocialProof />
      </Suspense>
    </div>
  );
};

export default Index;
